import React from 'react';
import { Link } from 'react-router-dom';
import { Youtube, Instagram, Facebook, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import { channels } from '../data/channels';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { name: 'YouTube', icon: Youtube, url: 'https://www.youtube.com/@VRMediaNetwork.Official' },
    { name: 'Instagram', icon: Instagram, url: 'https://www.instagram.com/vr_media_network/' },
    { name: 'Facebook', icon: Facebook, url: 'https://www.facebook.com/vrmediaofficial2023' },
  ];

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Channels', path: '/channels' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <footer className="bg-vr-darker border-t border-gray-800">
      <div className="container-custom">
        <div className="section-padding">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <div className="mb-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 rounded-lg overflow-hidden shadow-lg">
                    <img 
                      src="/VRM Logo PNG.png" 
                      alt="VR Media Network Logo"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full bg-vr-red flex items-center justify-center text-white font-bold text-sm" style={{display: 'none'}}>
                      VRM
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold font-display text-white">
                    VR Media Network
                  </h3>
                </div>
                <p className="text-gray-400 mb-4">
                  Creating Stories. Sharing Moments.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  We manage multiple YouTube channels across diverse niches, 
                  bringing you quality content that inspires, educates, and entertains.
                </p>
              </div>
              
              {/* Social Links */}
              <div className="flex space-x-4">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.9 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className="p-2 bg-gray-800 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors duration-200"
                  >
                    <motion.div
                      whileHover={{ rotate: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <social.icon size={20} />
                    </motion.div>
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className="text-gray-400 hover:text-white transition-colors duration-200"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Channels */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-4">Our Channels</h4>
              <ul className="space-y-3">
                {channels.map((channel) => (
                  <li key={channel.id}>
                    <a
                      href={channel.youtubeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors duration-200 flex items-center space-x-3"
                    >
                      <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-800 flex items-center justify-center shadow-md">
                        <img 
                          src={channel.logo} 
                          alt={`${channel.name} logo`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-full h-full bg-gray-700 flex items-center justify-center text-white text-xs font-bold" style={{display: 'none'}}>
                          {channel.name.split(' ').map(word => word[0]).join('')}
                        </div>
                      </div>
                      <span className="text-sm">{channel.name}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-gray-400 text-sm">
                © {currentYear} VR Media Network. All rights reserved.
              </p>
              <div className="flex items-center space-x-4 text-sm text-gray-400">
                <a href="/privacy" className="hover:text-white transition-colors">
                  Privacy Policy
                </a>
                <a href="/terms" className="hover:text-white transition-colors">
                  Terms of Service
                </a>
                        <a href="mailto:admin@vr-media.in" className="flex items-center space-x-1 hover:text-white transition-colors">
          <Mail size={16} />
          <span>Contact</span>
        </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
